import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

// The `lock` glyph (app/assets/config/icons.json), inlined because Tom Select
// builds the chips in JS — there is no ERB pass to render an icon into. Same
// approach as the grip in ui--tag-input-sortable.
const LOCK_SVG = `<svg class="ui-tag-lock ml-1 shrink-0" width="12" height="12" viewBox="0 0 18 19"
  fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
  aria-hidden="true" focusable="false"><path d="M4.5 7.8V6.1C4.5 3.28335 6.51472 1 9 1C11.0969 1 12.8587 2.62534 13.3583 4.825M9 11.2V14.6M1.5 12.9C1.5 10.4959 1.5 9.29379 2.15901 8.5469C2.81802 7.8 3.87868 7.8 6 7.8H12C14.1213 7.8 15.182 7.8 15.841 8.5469C16.5 9.29379 16.5 10.4959 16.5 12.9C16.5 15.3041 16.5 16.5062 15.841 17.2531C15.182 18 14.1213 18 12 18H6C3.87868 18 2.81802 18 2.15901 17.2531C1.5 16.5062 1.5 15.3041 1.5 12.9Z"/></svg>`

// Connects to data-controller="ui--select"
export default class extends Controller {
  static targets = ["select"]
  static values = {
    options: Object,
  }

  connect() {
    const userOptions = this.optionsValue || {}
    const isMultiple = this.isMultiple(userOptions)

    // Values the caller pinned (Ui::TagInputComponent's locked_values): the chip
    // renders without a remove affordance and Tom Select refuses to delete it.
    // The remove button and Backspace both go through `shouldDelete`, so one
    // `onDelete` veto covers both.
    const lockedValues = new Set(userOptions.lockedValues ?? [])
    const isLocked = (value) => lockedValues.has(String(value))

    const config = {
      ...(lockedValues.size > 0 && {
        onDelete: (values) => !values.some(isLocked),
      }),

      // Tom Select core configuration
      // When true, Tom Select keeps <option value=""> rendered by the server.
      // Essential for filter selects with an "All X" sentinel — otherwise Tom Select
      // strips that option on init, causing a 1-2s flicker as the native render gives
      // way to an empty Tom Select UI.
      //
      // It's also required for non-searchable single selects: those pass
      // `controlInput: null` (below), which leaves Tom Select with no DOM input to
      // carry the placeholder. Keeping the server's `<option value="">placeholder</option>`
      // lets it render as the muted placeholder item instead (see the
      // `.ts-control .item[data-value=""]` rule in application.css) — without this the
      // control renders empty and collapses.
      allowEmptyOption:
        userOptions.allowEmptyOption === true ||
        (!userOptions.searchable && !isMultiple),
      placeholder: userOptions.placeholder || "Select...",
      // Free-form creation is opt-in per caller (Ui::TagInputComponent). With it
      // on, a typed value becomes a tag on Enter, on a comma, or on blur — which
      // is what Tab does. Without `delimiter`/`createOnBlur` the only way in is
      // Enter, and a form-level Enter would submit instead. `persist: false` keeps
      // a removed tag from lingering in the dropdown as a suggestion.
      create: userOptions.create === true,
      ...(userOptions.create === true && {
        delimiter: ",",
        createOnBlur: true,
        persist: false,
      }),
      // Opt-in client-side gate on created values (an email pattern from
      // Ui::TagInputComponent's create_filter). A RegExp source string over JSON;
      // a typed value that doesn't match is not created (trimmed first so an
      // email's surrounding whitespace doesn't block it). The server stays the
      // authoritative validator — this only stops obvious garbage becoming a tag.
      ...(userOptions.create === true &&
        typeof userOptions.createFilter === "string" && {
          createFilter: (input) =>
            new RegExp(userOptions.createFilter).test(input.trim()),
        }),
      maxItems: userOptions.multiple ? null : userOptions.maxItems || 1,
      // ponytail: null = render all options (Tom Select defaults to 50). Per-select
      // override via maxOptions if a huge list ever needs capping again.
      maxOptions: userOptions.maxOptions ?? null,

      // Detach the dropdown to a different parent (e.g. "body") so it escapes an
      // ancestor clipping context. Tom Select positions a body-detached dropdown
      // itself, so the custom placement below is skipped when this is set.
      ...(userOptions.dropdownParent && {
        dropdownParent: userOptions.dropdownParent,
      }),

      // Plugins
      plugins: this.getPlugins(userOptions),

      // Sorting
      sortField:
        userOptions.sortable !== false
          ? { field: "text", direction: "asc" }
          : null,

      // Searchability
      ...(!userOptions.searchable && { controlInput: null }),

      // Summary mode keeps selected options visible (checked) in the dropdown
      // so they stay removable even though the chips are hidden in the control.
      ...(this.isSummary(userOptions) && { hideSelected: false }),

      // Custom rendering for @a-safe-digital styling
      render: {
        optgroup_header: (data, escape) => {
          return `<div class="optgroup-header">${escape(data.label)}</div>`
        },
        option: (data, escape) => {
          const displayField = userOptions.loadUrl
            ? userOptions.labelField || "label"
            : "text"
          const text = data[displayField] || data.text || ""
          const dot = data.color
            ? `<span class="inline-block w-3 h-3 rounded mr-1.5 align-middle shrink-0" style="background:${data.color};border:1px solid rgba(0,0,0,0.1)"></span>`
            : ""
          // Optional secondary line (e.g. a contact's email) — v1 parity where the
          // ComboBox shows "Name <email>" so people with the same name are
          // distinguishable. Only rendered when the consumer sets secondaryField
          // and the loaded row carries that value (typed free-text tags don't).
          const secondary =
            userOptions.secondaryField && data[userOptions.secondaryField]
              ? ` <span class="text-foreground-medium">&lt;${escape(data[userOptions.secondaryField])}&gt;</span>`
              : ""
          // The label rides in one inline span: `.option` is a flex row, and a
          // bare text node next to Tom Select's <span class="highlight"> would
          // become its own flex item, losing the spaces at its edges ("HRIS
          // ›HiBobLogin Issues" while searching "hibob").
          return `<div class="option flex items-center">${dot}<span>${escape(text)}${secondary}</span></div>`
        },
        item: (data, escape) => {
          const displayField = userOptions.loadUrl
            ? userOptions.labelField || "label"
            : "text"
          const text = data[displayField] || data.text || ""
          const dot = data.color
            ? `<span class="inline-block w-3 h-3 rounded mr-1 align-middle shrink-0" style="background:${data.color};border:1px solid rgba(0,0,0,0.1)"></span>`
            : ""
          // Optional secondary line (e.g. a contact's email) — v1 parity where the
          // ComboBox shows "Name <email>" so people with the same name stay
          // distinguishable. Only when the consumer set secondaryField and the row
          // carries it (typed free-text tags don't).
          const secondary =
            userOptions.secondaryField && data[userOptions.secondaryField]
              ? ` <span class="text-foreground-medium">&lt;${escape(data[userOptions.secondaryField])}&gt;</span>`
              : ""
          // A locked chip is stamped for the CSS that hides its remove button and
          // for ui--tag-input-sortable, which skips its grip; the title is the
          // hover explanation.
          const locked = isLocked(data.value)
          const title = userOptions.lockedTitle
            ? ` title="${escape(userOptions.lockedTitle)}"`
            : ""
          const lockedAttrs = locked
            ? ` data-locked="true" aria-readonly="true"${title}`
            : ""
          return `<div class="item flex items-center"${lockedAttrs} data-value="${escape(data.value)}">${dot}${escape(text)}${secondary}${locked ? LOCK_SVG : ""}</div>`
        },
        // Empty-search row. Generic English by default; a caller that knows
        // what is being searched passes a localized noResultsText (e.g.
        // "No categories found").
        no_results: (_data, escape) => {
          const text = escape(userOptions.noResultsText || "No results found")
          return `<div class="no-results">${text}</div>`
        },
        // Create row for tag inputs (create: true). Tom Select's default is a
        // bare English "Add <b>x</b>…" div; this styles it like an option and
        // lets the caller localize the verb via createLabel.
        //
        // The English "Add" fallback is only reachable for callers that pass
        // BOTH options and create — with no options the whole dropdown is
        // hidden (.ts-tags-no-suggestions below), create row included, which
        // covers the contacts free-form inputs (emails/phones/websites). Any
        // caller that shows suggestions should pass a localized createLabel,
        // as the support tag picker does.
        option_create: (data, escape) => {
          const label = escape(userOptions.createLabel || "Add")
          return `<div class="create">${label} <strong>${escape(data.input)}</strong>&hellip;</div>`
        },
        loading: () => {
          return '<div class="loading">Loading...</div>'
        },
      },
    }

    // Add remote data loading if loadUrl is provided
    if (userOptions.loadUrl) {
      config.valueField = userOptions.valueField || "value"
      config.labelField = userOptions.labelField || "label"
      config.searchField = userOptions.searchField || ["label"]

      // The server is authoritative when loading remotely. Tom Select's
      // default sifter would re-score and hide options whose label doesn't
      // match the typed query — that would silently drop results matched
      // server-side by `last_name`/`full_name` when the label is built from
      // a different field. Forcing score=1 keeps every server option.
      config.score = () => () => 1

      // Tom Select's `preload` triggers an initial load() call so the dropdown
      // is populated before the user types. Accepted values: true | "focus".
      if (userOptions.preload !== undefined) {
        config.preload = userOptions.preload
      }

      config.load = (query, callback) => {
        const url = new URL(userOptions.loadUrl, window.location.origin)
        // Send both param names: PackSupport::SearchParams endpoints read
        // `query`, others read `q`. `q` alone left the query nil server-side, so
        // remote pickers returned unfiltered results (CONEK-4230).
        url.searchParams.append("q", query)
        url.searchParams.append("query", query)

        fetch(url.toString(), {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
        })
          .then((response) => response.json())
          .then((data) => {
            // Accept the common paginated shapes servers use, not just a bare
            // array or `{options}`: the contact/user/task search endpoints nest
            // rows under `results` (mirrors ui--multi-select's own fallback).
            const items =
              data.options || data.results || data.items || data.data || data
            // Tom Select keeps options from previous load() calls in its
            // internal dict, so without this wipe each keystroke would
            // append the new server-filtered set on top of stale rows from
            // the preload + earlier queries. Keep currently selected items.
            const ts = this.tomSelect
            if (ts) {
              ts.clearOptions((_option, value) => ts.items.indexOf(value) > -1)
            }
            callback(items)
          })
          .catch(() => {
            callback()
          })
      }
    }

    // Initialize Tom Select
    this.tomSelect = new TomSelect(this.selectTarget, config)

    // Non-searchable single selects force allowEmptyOption so the server's
    // <option value="">placeholder</option> renders as the muted placeholder
    // item in the control (see connect() comment above). The side effect is
    // that Tom Select also keeps that empty option in the dropdown as a
    // selectable row. Flag those instances so CSS can hide the empty option
    // from the dropdown only — the control's placeholder item is untouched.
    // Sentinel "All X" filters (explicit allowEmptyOption: true) are excluded,
    // since their empty option IS a valid choice that must stay listed.
    const isPlaceholderEmpty =
      userOptions.allowEmptyOption !== true &&
      !userOptions.searchable &&
      !isMultiple
    if (isPlaceholderEmpty) {
      this.tomSelect.wrapper.classList.add("ts-placeholder-empty")
    }

    // A free-form tag input with nothing to suggest: hide the dropdown (CSS), so
    // typing does not surface an empty "no options" list. Tagged rather than
    // disabled outright because Tom Select routes Enter through the dropdown's
    // active option, so it has to stay in the DOM.
    if (userOptions.create === true && userOptions.hasSuggestions === false) {
      this.tomSelect.wrapper.classList.add("ts-tags-no-suggestions")
    }

    // In multi-select mode Tom Select keeps the typed search text after
    // picking an option, forcing the user to clear it manually before the
    // next search. Opt out per instance with clearSearchOnSelect: false.
    if (
      this.isMultiple(userOptions) &&
      userOptions.clearSearchOnSelect !== false
    ) {
      this.tomSelect.on("item_add", () => {
        this.tomSelect.setTextboxValue("")
        this.tomSelect.refreshOptions(false)
      })
    }

    // Tom Select's `preload` only fires the initial load() once. If the user
    // searches, blurs, and reopens with an empty input, the dropdown sticks
    // on the prior filtered results. Re-fire the empty-query load on every
    // subsequent open when the consumer opted into `preload`.
    if (userOptions.loadUrl && userOptions.preload) {
      let firstOpen = true
      this.tomSelect.on("dropdown_open", () => {
        if (firstOpen) {
          firstOpen = false
          return
        }
        const ts = this.tomSelect
        if (!ts.control_input || ts.control_input.value === "") {
          ts.lastQuery = null
          ts.load("")
        }
      })
    }

    // Summary mode: render a single-line "N label" count in the control
    // instead of wrapping chips, so the filter keeps a fixed height.
    if (this.isSummary(userOptions)) {
      this.setupSummary(userOptions)
    }

    // Placeholder mode (hideEmptyOption): a non-searchable single select passes
    // controlInput: null, so Tom Select has no input to show the placeholder and
    // renders no item for the empty <option> — the control is blank. Expose the
    // placeholder text on the control so CSS paints it via ::before while the
    // control is empty (see `.ui-select-hide-empty-option ... .ts-control:empty`
    // in application.css). Cleared automatically once a real item is rendered.
    if (userOptions.hideEmptyOption && !isMultiple) {
      this.tomSelect.control.setAttribute(
        "data-placeholder",
        userOptions.placeholder || "Select...",
      )
    }

    // Dropdown placement. TomSelect always renders the dropdown below the
    // control and has no collision detection, so near the bottom of the
    // viewport it opens downward and clips off-screen. `dropUp: true` forces
    // it above; otherwise we auto-flip: open upward only when the dropdown
    // doesn't fit below AND there's more room above.
    this.tomSelect.on("dropdown_open", (dropdown) => {
      // A body-detached dropdown is positioned by Tom Select against the
      // control's viewport rect; overriding top/bottom here would break it.
      if (userOptions.dropdownParent) return
      if (userOptions.dropUp) {
        this.placeDropdown(dropdown, true)
        return
      }
      const controlRect = this.tomSelect.control.getBoundingClientRect()
      const spaceBelow = window.innerHeight - controlRect.bottom
      const spaceAbove = controlRect.top
      const dropdownHeight = dropdown.getBoundingClientRect().height
      const shouldDropUp =
        dropdownHeight > spaceBelow && spaceAbove > spaceBelow
      this.placeDropdown(dropdown, shouldDropUp)
    })

    // Enter right after typing (CONEK-8029). Tom Select filters the list only
    // `refreshThrottle` (300 ms) after the last keystroke, but selects the
    // active option on Enter at once — before that refresh it is still the
    // first option of the unfiltered list, so "data" + Enter picked "Account
    // Management". Upstream closed it as not planned (orchidjs/tom-select#764).
    // Run the pending refresh first, so Enter picks what was typed. Capture
    // phase on the wrapper, so this runs before Tom Select's own keydown
    // listener on the input.
    //
    // Local options only: with `loadUrl` the refresh starts a fetch, and Enter
    // still lands on the previous results until it resolves.
    // `refreshTimeout` and `_onInput` are Tom Select internals (the host
    // compiles this against its own tom-select); if a future version drops
    // them, this does nothing and Enter falls back to Tom Select's behaviour.
    this.tomSelect.wrapper.addEventListener(
      "keydown",
      (event) => {
        if (event.key !== "Enter" || event.isComposing) return
        const ts = this.tomSelect
        if (!ts?.refreshTimeout || typeof ts._onInput !== "function") return
        window.clearTimeout(ts.refreshTimeout)
        ts.refreshTimeout = null
        ts._onInput()
      },
      true,
    )

    // Handle disabled state
    if (userOptions.disabled) {
      this.tomSelect.disable()
    }

    // Add error class if needed
    if (this.selectTarget.hasAttribute("aria-invalid")) {
      this.element.classList.add("error")
    }
  }

  /**
   * Replace this select's options at runtime.
   *
   * For a select whose choices are not known when the page is rendered — the
   * Drawing v2 barrier panel, where the available colours and materials come
   * from the selected barrier's catalog variant and change with every cascade.
   *
   * `event.detail.options` is a list of `[label, value]` pairs, matching the
   * shape `Ui::SelectComponent` already takes server-side, so a caller
   * describes its options one way whichever side fills them.
   * `event.detail.value` is what should end up selected.
   *
   * Reaching into `this.tomSelect` from another controller would be reaching
   * into design-system internals; this is the door. It also mirrors v1, where
   * a select with one option or none is not a choice and is disabled.
   */
  setOptions(event) {
    if (!this.tomSelect) return

    const options = event.detail?.options ?? []
    const value = event.detail?.value ?? null

    this.tomSelect.clearOptions()
    for (const [label, optionValue] of options) {
      this.tomSelect.addOption({ value: optionValue, text: label })
    }
    this.tomSelect.refreshOptions(false)

    // `silent` — this is the panel telling the select what the entity holds,
    // not the user choosing. Without it the change handler fires and the panel
    // writes the value it just read back onto the entity.
    this.tomSelect.setValue(value ?? "", true)

    if (options.length > 1) this.tomSelect.enable()
    else this.tomSelect.disable()
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy()
    }
  }

  placeDropdown(dropdown, dropUp) {
    if (dropUp) {
      dropdown.style.top = "auto"
      dropdown.style.bottom = "100%"
    } else {
      dropdown.style.bottom = "auto"
      dropdown.style.top = "100%"
    }
  }

  isMultiple(userOptions) {
    return (
      userOptions.multiple || userOptions.plugins?.includes("remove_button")
    )
  }

  isSummary(userOptions) {
    return this.isMultiple(userOptions) && !!userOptions.summaryLabel
  }

  getPlugins(userOptions) {
    const plugins = {}

    if (this.isMultiple(userOptions)) {
      if (this.isSummary(userOptions)) {
        // Chips are hidden in summary mode, so removal happens by toggling the
        // checkbox in the dropdown rather than via a per-chip remove button.
        plugins.checkbox_options = {}
      } else {
        plugins.remove_button = { title: "Remove this item" }
      }
    }

    // Add clear_button if clearable
    if (userOptions.clearable) {
      plugins.clear_button = { title: "Clear selection" }
    }

    return plugins
  }

  // Drives the single-line count summary shown in the control. The actual
  // chips/input are hidden via CSS (.ts-summary); we expose the count through
  // a `data-summary` attribute that CSS renders with `content: attr(...)`.
  setupSummary(userOptions) {
    const ts = this.tomSelect
    ts.wrapper.classList.add("ts-summary")

    const placeholder = userOptions.placeholder || "Select..."
    const update = () => {
      const count = ts.items.length
      if (count === 0) {
        ts.control.setAttribute("data-summary", placeholder)
        ts.control.classList.add("is-empty")
      } else {
        // A single selection reads clearer as the option's own name than as
        // "1 branch"; multiple collapse to the "N branches" count.
        let text
        if (count === 1) {
          const option = ts.options[ts.items[0]]
          text = (option && option.text) || ts.items[0]
        } else {
          const noun =
            userOptions.summaryLabelPlural || userOptions.summaryLabel
          text = `${count} ${noun}`
        }
        ts.control.setAttribute("data-summary", text)
        ts.control.classList.remove("is-empty")
      }
    }

    ts.on("item_add", update)
    ts.on("item_remove", update)
    ts.on("clear", update)
    update()
  }
}
